import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, Volume2, VolumeX } from "lucide-react";
import {
    pushSupported, pushPermission, ensureRegistration,
    getExistingSubscription, enablePush, disablePush, sendTestPush,
    getChimePref, setChimePref, playChime,
} from "@/lib/push";

/**
 * A compact toggle card for enabling / disabling web push notifications.
 * Drop it anywhere on a page (dashboard, profile, settings).
 */
export default function PushToggleCard() {
    const [supported, setSupported] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [permission, setPermission] = useState("default");
    const [busy, setBusy] = useState(false);
    const [chime, setChime] = useState(getChimePref());

    const toggleChime = () => {
        const next = !chime;
        setChime(next);
        setChimePref(next);
        if (next) playChime(); // preview when turning it on
    };

    useEffect(() => {
        const sup = pushSupported();
        setSupported(sup);
        if (!sup) return;
        (async () => {
            try {
                await ensureRegistration();
                const sub = await getExistingSubscription();
                setEnabled(Boolean(sub));
                setPermission(pushPermission());
            } catch (_) {}
        })();
    }, []);

    const onEnable = async () => {
        setBusy(true);
        try {
            await enablePush();
            setEnabled(true);
            setPermission(pushPermission());
            toast.success("Notifications enabled");
        } catch (e) {
            toast.error(e.message || "Couldn't enable notifications");
        } finally { setBusy(false); }
    };

    const onDisable = async () => {
        setBusy(true);
        try {
            await disablePush();
            setEnabled(false);
            toast.success("Notifications turned off");
        } catch (e) {
            toast.error(e.message || "Couldn't turn off notifications");
        } finally { setBusy(false); }
    };

    const onTest = async () => {
        setBusy(true);
        try {
            await sendTestPush();
            toast.success("Test push sent — check your phone in a second");
        } catch (e) {
            toast.error(e.message || "Couldn't send test push");
        } finally { setBusy(false); }
    };

    if (!supported) {
        return (
            <div className="card-flat p-5" data-testid="push-unsupported">
                <div className="flex items-start gap-3">
                    <BellOff size={18} className="text-[var(--text-muted)] mt-0.5" />
                    <div className="text-sm">
                        <div className="font-semibold">Push notifications unavailable</div>
                        <div className="text-[var(--text-muted)] mt-1">
                            On iPhone, first install SCS: tap <b>Share → Add to Home Screen</b>, then open the installed app to enable notifications.
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="card-flat p-5" data-testid="push-toggle-card">
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                    {enabled ? (
                        <BellRing size={18} className="text-[var(--pine)] mt-0.5 shrink-0" />
                    ) : (
                        <Bell size={18} className="text-[var(--text-muted)] mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                        <div className="font-semibold text-sm">Job notifications</div>
                        <div className="text-xs text-[var(--text-muted)] mt-1">
                            {enabled
                                ? "You'll get a push the moment a job is assigned or rescheduled."
                                : "Turn on push and we'll ping you the moment a job is assigned or moved."}
                            {permission === "denied" && (
                                <span className="block mt-1 text-red-600">Blocked — enable notifications for this app in your browser or phone settings.</span>
                            )}
                        </div>
                    </div>
                </div>
                {enabled ? (
                    <div className="flex items-center gap-2 shrink-0">
                        <button data-testid="push-test-btn" onClick={onTest} disabled={busy}
                                className="btn-ghost text-xs">
                            Test
                        </button>
                        <button data-testid="push-disable-btn" onClick={onDisable} disabled={busy}
                                className="btn-ghost text-sm">
                            {busy ? "…" : "Turn off"}
                        </button>
                    </div>
                ) : (
                    <button data-testid="push-enable-btn" onClick={onEnable}
                            disabled={busy || permission === "denied"}
                            className="btn-primary text-sm inline-flex items-center gap-2">
                        {busy ? "Enabling…" : (<><Bell size={14} /> Enable</>)}
                    </button>
                )}
            </div>

            {/* Chime + vibrate row */}
            <div className="mt-4 pt-4 border-t border-[var(--border-soft)] flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    {chime ? (
                        <Volume2 size={16} className="text-[var(--pine)] shrink-0" />
                    ) : (
                        <VolumeX size={16} className="text-[var(--text-muted)] shrink-0" />
                    )}
                    <div className="text-xs text-[var(--text-muted)] min-w-0">
                        <b className="text-[var(--text-primary)]">Chime & vibrate</b> — a soft ding-ding plays when the app is open. Phones vibrate on native pushes.
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button data-testid="chime-preview-btn" onClick={() => playChime()}
                            className="btn-ghost text-xs" title="Preview the chime">
                        Preview
                    </button>
                    <button data-testid="chime-toggle-btn" onClick={toggleChime}
                            role="switch" aria-checked={chime}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${chime ? "bg-[var(--pine)]" : "bg-[var(--border-soft)]"}`}>
                        <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${chime ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                </div>
            </div>
        </div>
    );
}
